import axios, { AxiosError, type AxiosRequestConfig, type InternalAxiosRequestConfig } from 'axios';
import { Message } from '@arco-design/web-react';
import type { ApiEnvelope } from '@/types/api';
import { translateErrorCode } from '@/utils/errorCode';

/** 后端基础地址（开发环境通过 vite proxy 转发） */
const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api';
const TIMEOUT = Number(import.meta.env.VITE_API_TIMEOUT ?? 15000);

const ACCESS_TOKEN_KEY = 'up_card_access_token';

export function setAccessToken(token: string | null) {
  if (token) localStorage.setItem(ACCESS_TOKEN_KEY, token);
  else localStorage.removeItem(ACCESS_TOKEN_KEY);
}

export function getAccessToken(): string | null {
  return localStorage.getItem(ACCESS_TOKEN_KEY);
}

export const httpClient = axios.create({
  baseURL: BASE_URL,
  timeout: TIMEOUT,
  withCredentials: false,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
});

httpClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = getAccessToken();
  if (token) {
    config.headers.set('Authorization', `Bearer ${token}`);
  }
  return config;
});

httpClient.interceptors.response.use(
  (response) => {
    const envelope = response.data as ApiEnvelope<unknown>;
    if (envelope && typeof envelope === 'object' && 'code' in envelope) {
      if (envelope.code !== '0' && envelope.code !== 'OK') {
        const friendly = translateErrorCode(envelope.code, envelope.message);
        Message.error(friendly);
        return Promise.reject(
          Object.assign(new Error(friendly), {
            isBusinessError: true,
            envelope,
          }),
        );
      }
    }
    return response;
  },
  (error: AxiosError<ApiEnvelope<unknown>>) => {
    if (error.response?.status === 401) {
      setAccessToken(null);
      Message.error('登录已失效，请重新登录');
      window.location.href = '/login';
      return Promise.reject(error);
    }
    const envelope = error.response?.data;
    const friendly = translateErrorCode(
      envelope?.code,
      envelope?.message || error.message,
    );
    Message.error(friendly);
    return Promise.reject(error);
  },
);

/** 统一拆封 envelope，业务层只关心 data */
export async function request<T>(config: AxiosRequestConfig): Promise<T> {
  const res = await httpClient.request<ApiEnvelope<T>>(config);
  return res.data.data;
}
